import { ReactNode, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import { Prompt, useHistory } from 'react-router';
import { DragDropContext } from 'react-beautiful-dnd';
import { FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import { Snippet } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import DetailsTableForm from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import { PageTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import Button, { ButtonSize } from 'packages/kakapo-ui-kit/components/editors/Button';
import ArchiveButton from 'packages/kakapo-ui-kit/components/common/ArchiveButton';

import TalentReviewChanges from './TalentReviewChanges';
import TalentReviewGrid from './TalentReviewGrid';
import TalentReviewMotivations from './TalentReviewMotivations';
import TalentReviewProgress from './TalentReviewProgress';
import TalentReviewTeam from './TalentReviewTeam';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';
import { TalentReviewUndo } from 'talent-reviews/TalentReviewUndo';

export function TalentReview(props: { backButton?: ReactNode; review: graphql.TalentReview }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const { isWorkspaceSuperuser } = useViewerRoles();

	const users = props.review.group.members.edges.map(({ node }) => node as graphql.User);

	const [lastTitle, setLastTitle] = useState(props.review.title);
	const [changeCount, setChangeCount] = useState(0);
	const [dragging, setDragging] = useState(false);

	const [temporaryPlacements, setTemporaryPlacements] = useState(props.review.placements);

	const [update, { loading }] = graphql.useUpdateTalentReviewMutation();

	const formik = useFormik({
		initialValues: {
			title: props.review.title,
		},
		onSubmit: () => {},
	});

	const titleChanged = formik.values.title !== lastTitle;

	async function onUpdate(placement: graphql.TalentReviewPlacement) {
		const old = temporaryPlacements.find((match) => match.user.id === placement.user.id);

		if (!old) {
			return;
		}

		if (old.column !== placement.column || old.row !== placement.row) {
			setTemporaryPlacements([
				placement,
				...temporaryPlacements.filter((match) => match.user.id !== placement.user.id),
			]);
		} else {
			setTemporaryPlacements(
				temporaryPlacements.map((match) =>
					match.user.id === placement.user.id ? placement : match,
				),
			);
		}

		if (
			(old.motivation !== placement.motivation || old.score !== placement.score) &&
			changeCount === 0
		) {
			setChangeCount(1);
		}
	}

	async function onUpdateMotivation(userID: string, motivation: string) {
		setTemporaryPlacements(
			temporaryPlacements.map((placement) => {
				if (placement.user.id === userID) {
					return { ...placement, motivation };
				} else {
					return placement;
				}
			}),
		);

		if (changeCount === 0) {
			setChangeCount(1);
		}
	}

	async function onSave(status: graphql.TalentReviewStatus) {
		setLastTitle(formik.values.title);
		setChangeCount(0);

		await update({
			variables: {
				input: {
					reviewID: props.review.id,
					title: formik.values.title,
					placements: temporaryPlacements.map(({ row, column, score, user, motivation }) => ({
						row,
						column,
						score,
						userID: user.id,
						motivation,
					})),
					status,
				},
			},
		});
	}

	return (
		<Box mr={5}>
			<Flex my={3}>
				<PageTitle>
					{props.review.viewerCanUpdate ? t('talentReviews.edit.title') : props.review.title}
				</PageTitle>

				{props.backButton}
			</Flex>

			{!props.review.viewerCanUpdate && props.review.template.description && (
				<Snippet maxHeight={100}>
					<QuillText quill={localize(props.review.template.description).text} />
				</Snippet>
			)}

			<Box mb={3}>
				<HorizontalRule />
			</Box>

			{props.review.viewerCanUpdate && (
				<>
					<Box maxWidth={600}>
						<FormikProvider value={formik}>
							<DetailsTableForm
								formValues={[
									{
										label: t('talentReviews.edit.titleLabel'),
										name: 'title',
										type: DetailsTableFormValueTypes.Text,
									},
								]}
							/>
						</FormikProvider>
					</Box>
					{props.review.template.description && (
						<Snippet maxHeight={100}>
							<QuillText quill={localize(props.review.template.description).text} />
						</Snippet>
					)}
					<Box my={3}>
						<HorizontalRule />
					</Box>
					<TalentReviewProgress review={props.review} placements={temporaryPlacements} />

					<Box mt={3}>
						<Prompt message={t('talentReviews.edit.unsavedChanges')} />
						<TalentReviewChanges
							count={changeCount + (titleChanged ? 1 : 0)}
							loading={loading}
							onSave={onSave}
						/>
					</Box>
				</>
			)}

			{isWorkspaceSuperuser && props.review.status === graphql.TalentReviewStatus.Shared && (
				<TalentReviewUndo {...props.review} />
			)}
			<DragDropContext
				onDragStart={() => setDragging(true)}
				onDragEnd={(result) => {
					setDragging(false);

					if (!result.destination?.droppableId.startsWith('cell-')) {
						return;
					}

					const motivation = temporaryPlacements.find(
						(placement) => placement.user.id === result.draggableId,
					)?.motivation;

					const placements = temporaryPlacements.filter(
						({ user }) => user.id !== result.draggableId,
					);

					const [row, column] = result.destination.droppableId
						.split('-')
						.slice(1)
						.map((x) => parseInt(x));

					const cell = placements.filter(
						(placement) => placement.row === row && placement.column === column,
					);

					const user = users.find((user) => user.id === result.draggableId)!;

					cell.splice(result.destination.index, 0, {
						user,
						row,
						column,
						motivation,
					});

					setTemporaryPlacements(
						placements
							.filter((placement) => !(placement.row === row && placement.column === column))
							.concat(cell),
					);

					setChangeCount(changeCount + 1);
				}}
			>
				{props.review.viewerCanUpdate && (
					<TalentReviewTeam
						temporaryPlacements={temporaryPlacements}
						users={users}
						readonly={!props.review.viewerCanUpdate}
					/>
				)}
				<TalentReviewGrid
					dragging={dragging}
					onUpdateMotivation={onUpdateMotivation}
					temporaryPlacements={temporaryPlacements}
					users={users}
					template={props.review.template}
					readonly={!props.review.viewerCanUpdate}
				/>
			</DragDropContext>
			<TalentReviewMotivations
				review={props.review}
				placements={temporaryPlacements}
				onUpdate={onUpdate}
			/>
			<Flex>
				<ArchiveButton
					item={props.review}
					useArchive={graphql.useArchiveTalentReviewMutation}
					useRestore={graphql.useRestoreTalentReviewMutation}
				/>
			</Flex>
		</Box>
	);
}

export default function TalentReviewByID(props: {
	type: 'department' | 'team';
	groupID: string;
	id: string;
}) {
	const { t } = useTranslation();

	const history = useHistory();
	const query = graphql.useTalentReviewQuery({ variables: { id: props.id } });

	return query.data ? (
		<TalentReview
			backButton={
				<Box flexGrow={0} flexShrink={0} ml={2}>
					<Button
						size={ButtonSize.Small}
						onClick={() => history.push(`/${props.type}/${props.groupID}/talent-reviews`)}
					>
						{t('talentReviews.edit.backButton')}
					</Button>
				</Box>
			}
			review={query.data.talentReview as graphql.TalentReview}
		/>
	) : (
		<Spinner size="18px" isLoading />
	);
}
